import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, watch, onMounted, onUnmounted, type Component } from 'vue'
  import { RouterView } from 'vue-router'
  import { useRoute, useRouter } from 'vue-router/composables'
  import * as Sentry from '@sentry/vue'
  import hexRgb from 'hex-rgb'
  import { DateTime } from 'luxon'

  import AppHeader from '@/components/AppHeader.vue'
  import AppFooter from '@/components/AppFooter.vue'
  import Modal from '@/components/common/Modal.vue'
  import FullScreenLoader from '@/components/common/FullScreenLoader.vue'
  import Confetti from '@/components/common/Confetti.vue'
  import CustomCompanyWidgets from '@/components/common/CustomCompanyWidgets.vue'

  // <---- Modals
  import ConfirmationModal from '@/components/modals/ConfirmationModal.vue'
  import YoutubeModal from '@/components/modals/YoutubeModal.vue'
  import InviteUserModal from '@/components/modals/InviteUserModal.vue'
  import AssignUserCourseModal from '@/components/modals/AssignUserCourseModal.vue'
  import AssignGroupCourseModal from '@/components/modals/AssignGroupCourseModal.vue'
  import AnswerQuestionResponseModal from '@/components/modals/AnswerQuestionResponseModal.vue'
  import LessonModal from '@/components/modals/LessonModal.vue'
  import CourseExamTimeoutModal from '@/components/modals/CourseExamTimeoutModal.vue'
  import FeedbackModal from '@/components/modals/FeedbackModal.vue'
  import LessonSlideshowModal from '@/components/course/LessonSlideshowModal.vue'
  import LoadingSpinner from '@/components/common/LoadingSpinner.vue'
  import StartCourseForFreeModal from '@/components/modals/StartCourseForFreeModal.vue'
  import SpeakToExpertModal from '@/components/modals/SpeakToExpertModal.vue'
  import BrowserExtensionModal from '@/components/modals/BrowserExtensionModal.vue'
  import GetStartedOnboardingModal from '@/components/modals/GetStartedOnboardingModal.vue'
  import AssignCourseGroupModal from '@/components/modals/AssignCourseGroupModal.vue'
  import SkgQuizModal from '@/components/modals/SkgQuizModal.vue'
  import FollowupModal from '@/components/course/followup/FollowupModal.vue'
  import CreateCourseModal from '@/components/modals/CreateCourseModal.vue'
  import CreateOrUpdateCourseGroupModal from '@/components/modals/CreateOrUpdateCourseGroupModal.vue'
  import EditOrDeleteCourseGroupModal from '@/components/modals/EditOrDeleteCourseGroupModal.vue'
  import MoveCourseToSectionModal from '@/components/modals/MoveCourseToSectionModal.vue'
  import ReferralCodeModal from '@/components/modals/ReferralCodeModal.vue'
  import ExamAppointmentModal from '@/components/modals/ExamAppointmentModal.vue'
  import CourseChangesModal from '@/components/modals/CourseChangesModal.vue'
  import IDDCoursesCheckoutModal from '@/components/modals/IDDCoursesCheckoutModal.vue'
  import GuidedQuizRegisterModal from '@/components/modals/GuidedQuizRegisterModal.vue'
  import LearningProgressPolicyModal from '@/components/modals/LearningProgressPolicyModal.vue'
  import CourseDetailsModal from '@/components/modals/CourseDetailsModal.vue'
  import CompanyOnboardingModal from '@/components/modals/CompanyOnboardingModal.vue'
  import WebinarModal from '@/components/modals/WebinarModal.vue'
  // ------>

  import useCommonMixin from '@/composables/useCommonMixin'
  import useI18n from '@/composables/useI18n'
  import useAuthentication from '@/composables/useAuthentication'
  import useBreakpoint from '@/composables/useBreakpoint'
  import useEditor from '@/composables/useEditor'
  import useCourse from '@/composables/useCourse'
  import useGamification from '@/composables/useGamification'
  import useToursAndHints from '@/composables/useToursAndHints'
  import useSegment from '@/composables/useSegment'
  import useEditorModeManager from '@/composables/useEditorModeManager'

  import ThemeApi from '@/services/api/ThemeApi'
  import UsersApi from '@/services/api/UsersApi'

  import { UserModule } from '@/store/modules/user'
  import { BookmarksModule } from '@/store/modules/Bookmarks'
  import { CourseModule } from '@/store/modules/course'
  import { EditorModule } from '@/store/modules/editor'

  import type { CompanyTheme, Theme } from '@/services/interfaces/Theme'
  import type { Company } from '@/services/interfaces/Auth0'
  import type { ModalPayload } from '@/services/interfaces/Common'
  import type { CoursePackage } from '@/services/interfaces/Course'

  import eventBus from '@/main'
  import CookieHelper from '@/helpers/CookieHelper'
  import auth_config from '../auth_config.json'
  import { TOURS_AND_HINTS } from '@/helpers/Constants'

  
export default /*#__PURE__*/_defineComponent({
  __name: 'App',
  setup(__props) {

  const { staging, production } = auth_config
  const tokenKey = process.env.APP_ENV === 'production' ? production.tokenKey : staging.tokenKey

  const route = useRoute()
  const router = useRouter()

  const {
    companySubdomainDetails,
    toKhebabCase,
    subDomainFromUrl,
    hasAccessToCompanySubdomain,
    hasAccessToCompany,
    redirectToNewDomain,
    iframeMode,
    highlightedCourseBundlePrefix,
    isSachkundeCompany,
    isKetchUpCompany,
    fromEmbeddedQuiz,
    hasValidUser,
    isLtiUser,
    is34ACompany,
  } = useCommonMixin()
  const { translateString } = useI18n()
  const { companyKey, redirect } = useAuthentication()
  const { isMobileDevice } = useBreakpoint()
  const { isEditor, inEditorMode } = useEditor(route)
  const {
    course,
    courseModules,
    fetchGroupCourses,
    hasPurchasedAtLeastACourse,
    findFreeModule,
    courseBundle,
    courseBundlesOverview,
    getPackageBundle,
    currentCourseId,
    onCourseLayout,
    onLogoutPage,
    sortedCourseBundlesOverview,
    currentlyAllowedCourseIds,
    navigateToCourseContent,
    navigateToModule,
    nextModuleToStart,
  } = useCourse()
  const { trackGamificationActivity, companyHasSetGamificationActivity, currentLevel, totalPoints } = useGamification()
  const { showTourAfterFirstMarch } = useToursAndHints()
  const { newGamificationLevelPopupShown } = useSegment()
  const { handleRouteChange, handleInitialRoute } = useEditorModeManager()

  const appKey = ref('app')
  const modalContentComponent = ref<Component | null>(null)
  const modalSuccessCallback = ref(undefined as undefined | (() => void))
  const modalErrorCallback = ref(undefined as undefined | (() => void))
  let modalCloseCallback = (callback: () => void) => {
    if (typeof callback === 'function') callback()
  }
  const modalProps = ref({})
  const hideCloseButton = ref(false)
  const closeModalOnOutsideClick = ref(true)
  const cssClass = ref('')
  const modalTitle = ref('')
  const showModal = ref(false)
  const showFullscreenLoader = ref(false)
  const showConfetti = ref(false)
  const hideCustomWidgets = ref(false)
  const calendlyWidgetHeight = ref(0)
  const showGetYourOwnAcademyBanner = ref(false)

  // Register all modal components for use with dynamic component
  const modalComponents = {
    ConfirmationModal,
    YoutubeModal,
    InviteUserModal,
    AssignUserCourseModal,
    AssignGroupCourseModal,
    AnswerQuestionResponseModal,
    LessonModal,
    CourseExamTimeoutModal,
    FeedbackModal,
    LessonSlideshowModal,
    LoadingSpinner,
    StartCourseForFreeModal,
    SpeakToExpertModal,
    BrowserExtensionModal,
    GetStartedOnboardingModal,
    AssignCourseGroupModal,
    SkgQuizModal,
    FollowupModal,
    CreateCourseModal,
    CreateOrUpdateCourseGroupModal,
    EditOrDeleteCourseGroupModal,
    MoveCourseToSectionModal,
    ReferralCodeModal,
    ExamAppointmentModal,
    CourseChangesModal,
    IDDCoursesCheckoutModal,
    GuidedQuizRegisterModal,
    LearningProgressPolicyModal,
    CourseDetailsModal,
    CompanyOnboardingModal,
    WebinarModal,
  }

  const loader = computed(() => document.getElementById('loader'))

  const loadingApp = computed(() => {
    return (
      !['Logout'].includes(route.name as string) &&
      (fetchingTheme.value ||
        route.name === 'LoginCallback' ||
        authPopupOpen.value ||
        (requiresAuth.value && !UserModule.isLoggedInUser))
    )
  })

  const shouldShowAppLoader = computed(() => {
    if (loadingApp.value) return true

    if (!hasValidUser.value || isAccessPage.value) return false

    if (isLtiUser.value) {
      return courseBundle.value === null || course.value === null || courseModules.value === undefined
    }

    if (fromEmbeddedQuiz.value) {
      return courseBundle.value === null && course.value === null
    }

    return false
  })

  const authPopupOpen = computed(() => {
    if (eventBus.$auth.popupOpen) return true
    return false
  })

  const requiresAuth = computed(() => route.matched.some((record: any) => record.meta.requiresAuth))

  const getCompany = computed(() => {
    if (UserModule.onboarded && UserModule.company) return UserModule.company
    return companySubdomainDetails.value || UserModule.company
  })

  const customCompanySubdomain = computed(() => {
    const companySubdomainDetailsFromCookie = CookieHelper.getCookieValue('company_subdomain_details') as Company | null
    if (!companySubdomainDetailsFromCookie) return UserModule.company?.subDomain
    return companySubdomainDetailsFromCookie.subDomain
  })

  const shouldRedirectToCustomDomain = computed(() => {
    return (
      subDomainFromUrl.value &&
      customCompanySubdomain.value &&
      subDomainFromUrl.value !== customCompanySubdomain.value &&
      !isAccessPage.value
    )
  })

  const isAccessPage = computed(() =>
    [
      'ProfilePage',
      'Login',
      'LoginCallback',
      'Logout',
      'CustomRegistration',
      'ResetPassword',
      'Signup',
      'CourseTrialPage',
    ].includes(route.name as string),
  )

  const isDashboardRoute = computed(() => route.name === 'Dashboard')

  const isOneGridRowRoute = computed(() => route.meta?.hideNav && route.meta?.centered)

  const isAcademySignup = computed(() => route.name === 'CustomRegistration' && isSachkundeCompany.value)

  const inCourseTrialPage = computed(() => route.name === 'CourseTrialPage')

  const inSetPrimaryEmailPage = computed(() => route.name === 'SetPrimaryEmail')

  const showCustomCompanyWidgets = computed(
    () => !isAccessPage.value && !hideWidgets.value && !loadingApp.value && !inEmbeddedQuiz.value,
  )

  const hideWidgets = computed(
    () => (['Module', 'Lesson'].includes(route.name as string) || hideCustomWidgets.value) && isMobileDevice.value,
  )

  const inEmbeddedQuiz = computed(() => route.name === 'EmbeddedGuidedQuiz')

  const emailIsVerified = computed(() => UserModule.emailVerified)

  const onboardingCheckReady = computed(
    () =>
      UserModule.currentCompany && UserModule.onboardingPages.length > 0 && hasValidUser.value && UserModule.onboarded,
  )

  const userHasFullyOnboarded = computed(() => {
    if (is34ACompany.value) {
      return hasValidUser.value && UserModule.onboarded
    }

    return (
      courseBundlesOverview.value &&
      hasValidUser.value &&
      UserModule.onboarded &&
      hasSetCourseTrial.value &&
      hasSetOrSkippedSettingExamAppointment.value
    )
  })

  const hasSetCourseTrial = computed(() => {
    return onboardingRequiresTrialSelection.value ? UserModule.hasSelectedTrial : true
  })

  const onboardingRequiresTrialSelection = computed(() =>
    UserModule.onboardingPages.some((page) => page.pageName === 'CourseTrialPage'),
  )

  const hasSetOrSkippedSettingExamAppointment = computed(() => {
    return onboardingRequiresSettingExamAppointment.value
      ? UserModule.company?.skipSettingExamAppointment || UserModule.hasSetExamDate
      : true
  })

  const onboardingRequiresSettingExamAppointment = computed(() =>
    UserModule.onboardingPages.some((page) => page.pageName === 'ExamDatePicker'),
  )

  const fullHeightRoutes = computed(() => ['CustomRegistration', 'PageNotFound'].includes(route.name as string))

  const enforceFullMaintenanceMode = async () => {
    const isEnabled = false
    const inFullMaintenanceMode = process.env.VUE_APP_ENABLE_COMPLETE_MAINTENANCE_MODE === 'true'

    if (inFullMaintenanceMode && route.name !== 'Maintenance' && isEnabled) {
      await UserModule.logout()
      return router.push('/maintenance')
    } else if (!inFullMaintenanceMode && route.name === 'Maintenance') {
      return router.replace({ path: '/' })
    }
    return Promise.resolve()
  }

  const setDefaultRootStyles = () => {
    const sheets = document.styleSheets
    sheets[sheets.length - 1]?.insertRule(
      [
        ':root {',
        '--tile-primary-shadow: 0px 0px 10px 6px rgba(var(--primary-color-rgb), 0.3);',
        '--tile-secondary-shadow: 0px 0px 10px 6px rgba(var(--secondary-color-rgb), 0.3);',
        '}',
      ].join(''),
    )
  }

  const bindRootEvents = () => {
    eventBus.$on('show-modal', (payload: ModalPayload) => {
      modalContentComponent.value = modalComponents[payload.modalContentComponent as keyof typeof modalComponents]
      modalSuccessCallback.value = payload.modalSuccessCallback
      modalErrorCallback.value = payload.modalErrorCallback
      modalCloseCallback = payload.modalCloseCallback
      modalProps.value = payload.modalProps || {}
      hideCloseButton.value = payload.hideCloseButton || false
      closeModalOnOutsideClick.value = payload.closeModalOnOutsideClick
      cssClass.value = payload.cssClass || ''
      modalTitle.value = payload.modalTitle || ''
      showModal.value = true
    })
    eventBus.$on('update-modal-title', (title: string) => {
      modalTitle.value = title
    })
    eventBus.$on('close-modal', () => {
      resetProps()
    })
    eventBus.$on('refresh', () => {
      appKey.value = Math.random().toString()
    })
    eventBus.$on('show-confetti', () => {
      showConfetti.value = true
    })
    eventBus.$on('hide-confetti', () => {
      showConfetti.value = false
    })
    eventBus.$on('turn-on-loader', () => {
      showFullscreenLoader.value = true
    })
    eventBus.$on('turn-off-loader', () => {
      showFullscreenLoader.value = false
    })
  }

  const resetProps = () => {
    showModal.value = false
    modalContentComponent.value = null
    modalSuccessCallback.value = undefined
    modalErrorCallback.value = undefined
    modalProps.value = {}
    modalTitle.value = ''
    hideCloseButton.value = false
    closeModalOnOutsideClick.value = true
  }

  const getDomainCompanyDetails = async () => {
    if (subDomainFromUrl.value || (!UserModule.onboarded && UserModule.companies.length === 0)) {
      let domain = ['production', 'qa'].includes(process.env.APP_ENV!) ? subDomainFromUrl.value : 'staging'
      if (route.query.testDomain) {
        domain = route.query.testDomain as string
      }

      try {
        const data = await UsersApi.getCompanies(domain!)
        if (data[0]) UserModule.setSubdomainCompany(data[0])
      } catch (error) {
        eventBus.$toasted.error(translateString('homepage.noCompanyFound', { domain }))
      }
    }
    return
  }

  const enforceSetUserProfile = async () => {
    const token = CookieHelper.getCookieValue(tokenKey)

    if (!token) return

    const isAcademySignup = CookieHelper.getCookieValue('is-academy-signup')

    if (UserModule.onboarded) return

    // Prevent redirects for certain routes
    if (['Logout', 'LoginCallback'].includes(route.name as string)) {
      return Promise.resolve()
    }

    if (isAcademySignup) {
      if (route.name === 'CustomRegistration') return

      // Redirect to registration page with any existing query parameters
      const uniqueParams = getQueryParams()
      return router.replace({
        path: '/registrierung',
        query: Object.fromEntries(uniqueParams.entries()),
      })
    } else if (!['Logout', 'ProfilePage'].includes(route.name!)) {
      return router.replace({ path: '/profile-page' })
    }
    return Promise.resolve()
  }

  const getQueryParams = () => {
    const query: { [key: string]: string } = {}
    const urlParams = new URLSearchParams(window.location.search)

    urlParams.forEach((value, key) => {
      query[key] = value
    })

    // Check for the redirect parameter and parse its URL parameters
    if (query.redirect) {
      const redirectUrl = new URL(query.redirect, window.location.origin)
      const redirectParams = new URLSearchParams(redirectUrl.search)

      redirectParams.forEach((value, key) => {
        // Override duplicates
        query[key] = value
      })

      // Remove the redirect parameter since it's not needed in the final URL
      delete query.redirect
    }

    if (highlightedCourseBundlePrefix.value) {
      query.bundlePrefix = highlightedCourseBundlePrefix.value
    }

    return new URLSearchParams(query)
  }

  const setUserCompany = async () => {
    let companyId = CookieHelper.getCookieValue(companyKey.value)
    // where only one company is returned, we can safely ignore the cookie value if not set
    if (companyId || (!companyId && UserModule.companies.length === 1)) {
      companyId = companyId ?? UserModule.companies[0].id
      UserModule.setCompany({ companyId })
    }
  }

  const redirectIfNotInCorrectSubdomain = async () => {
    // Redirection occurs based on the company a user belongs to
    if (!['production', 'qa'].includes(process.env.APP_ENV!)) return

    const company = UserModule.company
    const companyFromSubdomain = companySubdomainDetails.value
    const companyMismatch = companyFromSubdomain && company && companyFromSubdomain.id !== company.id

    if (companyMismatch) {
      if (hasAccessToCompanySubdomain.value || hasAccessToCompany.value) {
        handleSubdomainChange(hasAccessToCompanySubdomain.value ? companyFromSubdomain : company)
      } else {
        if (hasValidUser.value) await UserModule.logout()
        handleSubdomainChange(companyFromSubdomain)
      }
    } else if (shouldRedirectToCustomDomain.value) {
      /* for users who signed up through custom registration
              and are coming back from a previous domain
              */
      redirectToNewDomain(customCompanySubdomain.value!)
    }
  }

  const handleSubdomainChange = (company: Company) => {
    CookieHelper.setCrossDomainCookie(companyKey.value, company.id)
    redirectToNewDomain(company.subDomain)
  }

  const setUserCompanyBySelection = (toCompanySelection: { value: boolean }) => {
    if (!UserModule.company && !isAccessPage.value) {
      if (UserModule.companies.length > 1) {
        if (route.name === 'CompanySelection') return
        if (['staging', 'app'].includes(subDomainFromUrl.value!) || !hasAccessToCompanySubdomain.value) {
          toCompanySelection.value = true
          router.push(`/company-selection?redirect=${redirect.value}`).catch(() => {
            return
          })
          return
        }
      }
      UserModule.setCompany({ company: companySubdomainDetails.value! })
    }
    return
  }

  const fetchingTheme = ref(true)
  const setCompanyTheme = () => {
    if (getCompany.value?.id) {
      ThemeApi.getTheme(getCompany.value.id)
        .then(handleThemeResponse)
        .catch((error) => {
          Sentry.captureException(error)
        })
        .finally(() => {
          fetchingTheme.value = false
        })
    } else {
      fetchingTheme.value = false
    }
  }

  const handleThemeResponse = async (response: CompanyTheme) => {
    try {
      UserModule.setLogoUrls({
        desktopLogoUrl: response.logoUrl || '',
        mobileLogoUrl: response.mobileLogoUrl || '',
        headerImageUrl: response.headerImageUrl || '',
      })
      CookieHelper.setCrossDomainCookie('logoUrl', response.logoUrl || '')

      const theme: Theme = {
        colorVariants: toKhebabCase(response.colorVariants),
      }
      setRgbColors(theme)

      const styleElement = document.createElement('style')
      styleElement.id = 'company-theme-styles'

      let variantStyle = ':root {'
      for (const key in theme.colorVariants) {
        if (Object.prototype.hasOwnProperty.call(theme.colorVariants, key)) {
          const value = theme.colorVariants[key]
          if (value) {
            variantStyle = `${variantStyle}--${key}:${value};`
          }
        }
      }

      const fontStyles = await addExternalFont(response)
      styleElement.textContent = `${variantStyle}${fontStyles[0] ?? ''}${fontStyles[1] ?? ''}}`

      // Remove any existing theme styles
      const existingStyle = document.getElementById('company-theme-styles')
      if (existingStyle) {
        existingStyle.remove()
      }

      document.head.appendChild(styleElement)
    } catch (error) {
      Sentry.captureException(error)
    }
  }

  const setRgbColors = (theme: Theme) => {
    if (!theme?.colorVariants) return

    const themeProperties = [
      'primary-color',
      'secondary-color',
      'secondary-hover-color',
      'primary-text-color',
      'module-selector-background-color',
      'hero-foreground-color',
      'editor-primary-color',
    ]

    themeProperties.forEach((prop) => {
      try {
        if (theme.colorVariants[prop]) {
          const rgb = hexRgb(theme.colorVariants[prop])
          theme.colorVariants[`${prop}-rgb`] = `${rgb.red},${rgb.green},${rgb.blue}`
        }
      } catch (error) {
        Sentry.captureException(error)
      }
    })
  }

  const addExternalFont = (response: CompanyTheme): Promise<any> => {
    const { fontName, fontUrl, titleFontName, titleFontUrl } = response

    const primaryFont = new Promise((resolve) => {
      if (!fontUrl || !fontName) return resolve('')
      prependFontLinkToDocument(fontUrl)

      resolve(`--primary-font-name:${fontName};`)
    })

    const titleFont = new Promise((resolve) => {
      if (!titleFontUrl || !titleFontName) return resolve('--title-font-name:Big Daily Short')
      prependFontLinkToDocument(titleFontUrl)
      resolve(`--title-font-name:${titleFontName};`)
    })

    return Promise.all([primaryFont, titleFont])
  }

  const prependFontLinkToDocument = (fontUrl: string) => {
    const linkElement = getLinkElement(fontUrl)
    if (linkElement) linkElement.remove()

    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = fontUrl
    document.head.prepend(link)
  }

  const getLinkElement = (url: string) => {
    const links = document.getElementsByTagName('link')
    for (let i = 0; i < links.length; i++) {
      if (links[i].getAttribute('href') === url) {
        return links[i]
      }
    }
    return null
  }

  const handleLoggedInUserActions = async () => {
    if (!hasValidUser.value || !UserModule.currentCompany) return

    try {
      await Promise.all([BookmarksModule.getUserBookmarks(), UserModule.getCompanyGamificationActivities()])

      const { analytics } = window as any
      if (analytics) {
        analytics.identify(UserModule.auth0Id, {
          name: UserModule.fullname,
          email: UserModule.email,
          company: UserModule.currentCompany?.name ?? '',
          inviteSource: UserModule.inviteSource ?? '',
          created_at: DateTime.fromSQL(UserModule.createdAt),
        })
      }

      UserModule.getUserGamificationStats().finally(() => trackEmailVerifiedActivity())

      if (isEditor.value || UserModule.isCompanyAdmin) {
        await fetchGroupCourses()
      }
    } catch (error) {
      Sentry.captureException(error)
    }
  }

  const trackEmailVerifiedActivity = () => {
    const emailVerifiedGamificationActivityTracked = CookieHelper.getCookieValue('email_verified_tracked')

    if (
      emailIsVerified.value &&
      !emailVerifiedGamificationActivityTracked &&
      companyHasSetGamificationActivity.value('EMAIL_VERIFIED')
    ) {
      trackGamificationActivity('EMAIL_VERIFIED', null)
      CookieHelper.setCrossDomainCookie('email_verified_tracked', 'true')
    }
  }

  const grantOrDenyPublicAccess = () => {
    if (
      !isAccessPage.value &&
      !UserModule.isLoggedInUser &&
      UserModule.currentCompany &&
      !UserModule.currentCompany!.isPublic
    ) {
      router.push(`/login?redirect=${route.fullPath}`).catch(() => {
        return
      })
    }
  }

  const DEFAULT_BUNDLE_ID = '81d1fa5a-4be3-434c-bf2d-f50030c6c5c5' // Default 34i bundle ID

  const findBundleWithCourse = (courseId: string): string | null => {
    const bundle = sortedCourseBundlesOverview.value?.find((bundle) =>
      bundle.courses?.some((course) => course.id === courseId),
    )
    return bundle?.courseBundleId || null
  }

  const findBundleByLockedTrial = (): string | null => {
    if (!UserModule.lockedTrialBundle) return null

    const bundle = sortedCourseBundlesOverview.value?.find((b) => b.title.includes(UserModule.lockedTrialBundle))
    return bundle?.courseBundleId || null
  }

  const findBundleWithPurchasedCourses = (): string | null => {
    const bundleWithPurchasedCourse = sortedCourseBundlesOverview.value?.find((bundle) =>
      bundle.courses?.some((course) => course.purchased),
    )
    return bundleWithPurchasedCourse?.courseBundleId || null
  }

  const findBundleWithTrialCourses = (): string | null => {
    const bundleWithTrialCourse = sortedCourseBundlesOverview.value?.find((bundle) =>
      bundle.courses?.some((course) => course.trial),
    )
    return bundleWithTrialCourse?.courseBundleId || null
  }

  const determineBundleId = (): string => {
    // Priority 1: Current course route
    if (currentCourseId.value && currentlyAllowedCourseIds.value.includes(currentCourseId.value)) {
      const bundleId = findBundleWithCourse(currentCourseId.value)
      if (bundleId) return bundleId
    }

    // Priority 2: Locked trial bundle preference
    const lockedTrialBundleId = findBundleByLockedTrial()
    if (lockedTrialBundleId) return lockedTrialBundleId

    // Priority 3: Bundle with purchased courses
    const purchasedBundleId = findBundleWithPurchasedCourses()
    if (purchasedBundleId) return purchasedBundleId

    // Priority 4: Bundle with trial courses
    const trialBundleId = findBundleWithTrialCourses()
    if (trialBundleId) return trialBundleId

    // Fallback: Default bundle
    return DEFAULT_BUNDLE_ID
  }

  const selectCourseFromBundle = (bundle: CoursePackage, targetCourseId?: string): void => {
    if (!bundle?.courses?.length) return

    if (targetCourseId) {
      const course = bundle.courses.find((course) => course.id === targetCourseId)
      if (course) {
        CourseModule.setCourse(course)
        return
      }
    }

    const purchasedCourses = bundle.courses.filter((course) => course.purchased)

    if (purchasedCourses.length > 0) {
      const writtenCourse = purchasedCourses.find((course) => course.courseType === 'written')
      CourseModule.setCourse(writtenCourse || purchasedCourses[0])
    } else {
      const writtenCourse = bundle.courses.find((course) => course.courseType === 'written')
      if (writtenCourse) {
        CourseModule.setCourse(writtenCourse)
      }
    }
  }

  const preSelectCourseBundleAndCourse = async (): Promise<void> => {
    if (courseBundle.value) return

    try {
      const bundleId = determineBundleId()
      const bundle = await getPackageBundle(bundleId)

      if (bundle) {
        CourseModule.setCourseBundle(bundle)
        selectCourseFromBundle(bundle, currentCourseId.value)
      }
    } catch (error) {
      console.error('Error pre-selecting course bundle and course:', error)
      Sentry.captureException(error)
    }
  }

  const preFetch34ACourse = async () => {
    await CourseModule.getCourse({ courseId: 'c8165f08-2d46-4b56-b928-a246e6cd3568' })
  }

  const addVideoAskWidget = () => {
    if (UserModule.currentCompany?.videoAskScriptUrl) {
      const script = document.createElement('script')
      const videoAskEmbedScript = document.createElement('script')

      // Set the videoAsk script content
      script.textContent =
        'window.VIDEOASK_EMBED_CONFIG = {\n' +
        '  "kind": "widget",\n' +
        '  "url": "' +
        UserModule.currentCompany.videoAskScriptUrl +
        '",\n' +
        '  "options": {\n' +
        '    "widgetType": "VideoThumbnailExtraLarge",\n' +
        '    "text": "",\n' +
        '    "backgroundColor": "#7D00FE",\n' +
        '    "position": "bottom-right",\n' +
        '    "dismissible": true\n' +
        '  }\n' +
        '}'

      // Append the script element to the body
      document.body.appendChild(script)
      videoAskEmbedScript.src = 'https://www.videoask.com/embed/embed.js'
      document.body.appendChild(videoAskEmbedScript)
    }
  }

  const checkToReTriggerCompanyOnboardingPopup = async () => {
    if (!UserModule.currentCompany) return

    if (!UserModule.companyOnboardingPopupDetails) await UserModule.setCompanyOnboardingPopupDetails()
    /*
          we first the check if the company has configured the onboarding/intro properties;
          and if the user has already watched the intro video;
          we then allow them to re-trigger the popup by showing the button on the nav
        */
    if (UserModule.companyOnboardingPopupDetails && introVideoWatched.value) {
      UserModule.setReTriggerOnboardingPopup(true)
    }
  }

  const bootstrapApp = async () => {
    try {
      await enforceFullMaintenanceMode()

      setDefaultRootStyles()
      bindRootEvents()

      await Promise.all([getDomainCompanyDetails(), UserModule.getUser()])
      await enforceSetUserProfile()
      await setUserCompany()
      await redirectIfNotInCorrectSubdomain()

      const toCompanySelection = { value: false }
      setUserCompanyBySelection(toCompanySelection)
      setCompanyTheme()

      if (!toCompanySelection.value && !isAccessPage.value) {
        await handleLoggedInUserActions()
      }

      grantOrDenyPublicAccess()

      if (route.name) {
        handleInitialRoute(route.name as string)
      }

      if (getCompany.value?.id) {
        const companyId = getCompany.value.id

        await UserModule.getOnboardingFlowProperties(companyId)

        const companyFeatures = [
          getCompany.value.enableGlossaries && UserModule.setGlossaryItems(companyId),
          getCompany.value.enableOnboardingSurvey &&
            !getCompany.value.onboardingSurveyCompleted &&
            UserModule.setCompanyOnboardingSurveyQuestions(),
          !is34ACompany.value && !CourseModule.courseBundlesOverview?.length && CourseModule.getCourseBundlesOverview(),
        ]
        await Promise.all(companyFeatures.filter(Boolean))

        if (!inEditorMode.value) {
          if (is34ACompany.value) {
            await preFetch34ACourse()
          } else {
            await preSelectCourseBundleAndCourse()
          }
        }

        if (!hasPurchasedAtLeastACourse.value) {
          addVideoAskWidget()
        }

        await checkToReTriggerCompanyOnboardingPopup()

        UserModule.setLanguageTranslations()
        addCalendlyWidget()
        setMouseflowTrackingCode()
      }

      showTourAfterFirstMarch(setToursAndHintsCookie)
      checkToShowGetYourOwnAcademyBanner()
      downloadYTApi()
    } catch (error) {
      Sentry.captureException(error)
    }
  }

  const setWindowWidth = () => {
    UserModule.setWindowWidth(window.innerWidth)
  }

  const closeModal = () => {
    modalCloseCallback(() => {
      resetProps()
    })
  }

  const showOrHideWidgets = (action: 'show' | 'hide') => {
    switch (action) {
      case 'hide':
        hideCustomWidgets.value = true
        removeOrReInitializeCalendly('remove')
        return
      case 'show':
        hideCustomWidgets.value = false
        removeOrReInitializeCalendly('re-initialize')
        return
    }
  }

  const removeOrReInitializeCalendly = (action: 'remove' | 're-initialize') => {
    const calendlyDocument = document.getElementsByClassName('calendly-badge-content')[0] as HTMLDivElement
    switch (action) {
      case 'remove':
        if (calendlyDocument) {
          calendlyDocument.remove()
          calendlyWidgetHeight.value = 0
        }
        return
      case 're-initialize':
        // re-initialize calendly if it's not already there
        if (!calendlyDocument) {
          addCalendlyWidget()
        }
        return
    }
  }

  const addCalendlyWidget = async () => {
    const { Calendly } = window as any
    const calendlyWidget = UserModule.currentCompany?.widgets?.find((widget) => widget.type === 'calendly')
    if (
      calendlyWidget &&
      calendlyWidget.displayText &&
      calendlyWidget.url &&
      Calendly &&
      !isAccessPage.value &&
      !hideWidgets.value
    ) {
      Calendly.initBadgeWidget({
        url: calendlyWidget.url,
        text: calendlyWidget.displayText,
        color: 'var(--primary-color)',
        textColor: 'var(--primary-foreground-color)',
        branding: false,
      })
      const calendlyDocument = document.getElementsByClassName('calendly-badge-content')[0] as HTMLDivElement
      calendlyDocument.style.fontFamily = 'var(--primary-font-name)'
      calendlyWidgetHeight.value = calendlyDocument?.getBoundingClientRect().height
    }
  }

  const setMouseflowTrackingCode = async () => {
    if (UserModule.currentCompany && isSachkundeCompany.value) {
      // Initialize the Mouseflow queue
      ;(window as any)._mfq = (window as any)._mfq || []

      // we can add the mouseflow projectId on a company if we'll need to add to more companies
      const projectId = '6b2736ca-d260-405e-83f2-c2545f0d6eaf'

      // Create and append the Mouseflow script
      const mfScript = document.createElement('script')
      mfScript.type = 'text/javascript'
      mfScript.defer = true
      mfScript.src = `//cdn.mouseflow.com/projects/${projectId}.js`
      document.getElementsByTagName('head')[0].appendChild(mfScript)
    }
  }

  const setToursAndHintsCookie = () => {
    const cookie = CookieHelper.getCookieValue(TOURS_AND_HINTS)
    if (!cookie) {
      CookieHelper.setCookieValue(TOURS_AND_HINTS, 'true')
    }
  }

  const checkToShowGetYourOwnAcademyBanner = () => {
    const showBanner = CookieHelper.getCookieValue('hide-get-your-own-academy-banner')
    if (isSachkundeCompany.value || isKetchUpCompany.value) {
      if (showBanner === true) {
        showGetYourOwnAcademyBanner.value = false
      }
      showGetYourOwnAcademyBanner.value = true
    }
  }

  const downloadYTApi = () => {
    const tag = document.createElement('script')
    tag.src = 'https://www.youtube.com/iframe_api'
    const firstScriptTag = document.getElementsByTagName('script')[0] as HTMLScriptElement
    firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag)
  }

  const onResize = () => {
    setWindowWidth()
  }

  const enforceSettingCourseTrial = () => {
    if (!hasSetCourseTrial.value) {
      if (highlightedCourseBundlePrefix.value) {
        return router.replace({
          path: '/trial-selector',
          query: { bundlePrefix: highlightedCourseBundlePrefix.value },
        })
      }

      router.replace({ path: '/trial-selector' })
      return Promise.resolve()
    }
    return Promise.resolve()
  }

  const redirectToGuidedQuiz = async () => {
    try {
      const courseBundle = courseBundlesOverview.value?.find((bundle) => bundle.title.includes(fromEmbeddedQuiz.value!))

      if (!courseBundle) return

      const course = courseBundle.courses?.find((c) => c.courseType === 'written')

      if (!course) return

      const courseModules = CourseModule.courseModules(course.id)
      if (!courseModules.length) {
        await CourseModule.getCourseModules(course.id)
      }

      const moduleId = findFreeModule(course.id)?.id
      if (moduleId) {
        CookieHelper.removeCrossDomainCookie('fromEmbeddedQuiz')
        await router.push(`/courses/${course.id}/module/${moduleId}/guided-quiz`).catch(() => {
          return
        })
      }
    } catch (error) {
      Sentry.captureException(error)
    } finally {
      loader.value?.classList.remove('progress')
    }
  }

  const handleLtiUserAction = async () => {
    try {
      const nextLesson =
        nextModuleToStart.value?.lessons.find((lesson) => !lesson.completed) ??
        nextModuleToStart.value?.lessons[0] ??
        null

      if (course.value?.purchased || course.value?.expired) {
        await navigateToCourseContent(nextModuleToStart.value, nextLesson, course.value?.id, false)
      } else if (course.value?.trial) {
        await navigateToModule(course.value?.id, nextModuleToStart.value, nextLesson)
      }
    } catch (error) {
      Sentry.captureException(error)
    } finally {
      loader.value?.classList.remove('progress')
    }
  }

  bootstrapApp()
  setWindowWidth()

  onMounted(() => {
    window.addEventListener('resize', onResize)
  })

  onUnmounted(() => {
    window.onunhandledrejection = (event: PromiseRejectionEvent) => {
      eventBus.$logger.error('unhandled promise rejection: ', event, event.reason)
      event.preventDefault()
    }

    window.removeEventListener('resize', onResize)
  })

  watch(onboardingCheckReady, (value) => {
    if (value && !isAccessPage.value) {
      if (route.name !== 'CourseTrialPage') {
        enforceSettingCourseTrial()
      }
    }
  })

  watch(shouldShowAppLoader, async (shouldShow: boolean) => {
    if (shouldShow) {
      loader.value?.classList.add('progress')
    } else {
      if (hasValidUser.value && !isAccessPage.value) {
        if (fromEmbeddedQuiz.value && userHasFullyOnboarded.value) {
          await redirectToGuidedQuiz()
        } else if (isLtiUser.value) {
          await handleLtiUserAction()
        } else {
          loader.value?.classList.remove('progress')
        }
      } else {
        loader.value?.classList.remove('progress')
      }
    }
  })

  watch(inEditorMode, async (value) => {
    if (value) {
      await Promise.all([CourseModule.getTags(), EditorModule.setEditorAllAuthors()])
    } else {
      preSelectCourseBundleAndCourse()
    }
  })

  watch(hideWidgets, (value) => {
    if (value) {
      removeOrReInitializeCalendly('remove')
    } else {
      removeOrReInitializeCalendly('re-initialize')
    }
  })

  watch(
    () => route.name,
    (value, oldValue) => {
      const shouldRemoveCalendly = isAccessPage.value
      removeOrReInitializeCalendly(shouldRemoveCalendly ? 'remove' : 're-initialize')

      if (oldValue && value) {
        handleRouteChange(value, oldValue)
      }
    },
  )

  watch(
    () => currentLevel.value?.id,
    (newValue, oldValue) => {
      if (oldValue && newValue) {
        setTimeout(() => {
          showConfetti.value = true
          if (UserModule.currentCompany?.id && totalPoints.value && currentLevel.value?.name) {
            newGamificationLevelPopupShown(UserModule.currentCompany.id, currentLevel.value?.name, totalPoints.value)
          }
        }, 1000)
      }
    },
  )

  const introVideoWatched = computed(() => UserModule.getStartedChecklist?.introVideoWatched ?? false)

  watch(introVideoWatched, (value) => {
    if (value) {
      checkToReTriggerCompanyOnboardingPopup()
    }
  })

return { __sfc: true,staging, production, tokenKey, route, router, companySubdomainDetails, toKhebabCase, subDomainFromUrl, hasAccessToCompanySubdomain, hasAccessToCompany, redirectToNewDomain, iframeMode, highlightedCourseBundlePrefix, isSachkundeCompany, isKetchUpCompany, fromEmbeddedQuiz, hasValidUser, isLtiUser, is34ACompany, translateString, companyKey, redirect, isMobileDevice, isEditor, inEditorMode, course, courseModules, fetchGroupCourses, hasPurchasedAtLeastACourse, findFreeModule, courseBundle, courseBundlesOverview, getPackageBundle, currentCourseId, onCourseLayout, onLogoutPage, sortedCourseBundlesOverview, currentlyAllowedCourseIds, navigateToCourseContent, navigateToModule, nextModuleToStart, trackGamificationActivity, companyHasSetGamificationActivity, currentLevel, totalPoints, showTourAfterFirstMarch, newGamificationLevelPopupShown, handleRouteChange, handleInitialRoute, appKey, modalContentComponent, modalSuccessCallback, modalErrorCallback, modalCloseCallback, modalProps, hideCloseButton, closeModalOnOutsideClick, cssClass, modalTitle, showModal, showFullscreenLoader, showConfetti, hideCustomWidgets, calendlyWidgetHeight, showGetYourOwnAcademyBanner, modalComponents, loader, loadingApp, shouldShowAppLoader, authPopupOpen, requiresAuth, getCompany, customCompanySubdomain, shouldRedirectToCustomDomain, isAccessPage, isDashboardRoute, isOneGridRowRoute, isAcademySignup, inCourseTrialPage, inSetPrimaryEmailPage, showCustomCompanyWidgets, hideWidgets, inEmbeddedQuiz, emailIsVerified, onboardingCheckReady, userHasFullyOnboarded, hasSetCourseTrial, onboardingRequiresTrialSelection, hasSetOrSkippedSettingExamAppointment, onboardingRequiresSettingExamAppointment, fullHeightRoutes, enforceFullMaintenanceMode, setDefaultRootStyles, bindRootEvents, resetProps, getDomainCompanyDetails, enforceSetUserProfile, getQueryParams, setUserCompany, redirectIfNotInCorrectSubdomain, handleSubdomainChange, setUserCompanyBySelection, fetchingTheme, setCompanyTheme, handleThemeResponse, setRgbColors, addExternalFont, prependFontLinkToDocument, getLinkElement, handleLoggedInUserActions, trackEmailVerifiedActivity, grantOrDenyPublicAccess, DEFAULT_BUNDLE_ID, findBundleWithCourse, findBundleByLockedTrial, findBundleWithPurchasedCourses, findBundleWithTrialCourses, determineBundleId, selectCourseFromBundle, preSelectCourseBundleAndCourse, preFetch34ACourse, addVideoAskWidget, checkToReTriggerCompanyOnboardingPopup, bootstrapApp, setWindowWidth, closeModal, showOrHideWidgets, removeOrReInitializeCalendly, addCalendlyWidget, setMouseflowTrackingCode, setToursAndHintsCookie, checkToShowGetYourOwnAcademyBanner, downloadYTApi, onResize, enforceSettingCourseTrial, redirectToGuidedQuiz, handleLtiUserAction, introVideoWatched, computed, ref, watch, onMounted, onUnmounted, RouterView, useRoute, useRouter, Sentry, hexRgb, DateTime, AppHeader, AppFooter, Modal, FullScreenLoader, Confetti, CustomCompanyWidgets, ConfirmationModal, YoutubeModal, InviteUserModal, AssignUserCourseModal, AssignGroupCourseModal, AnswerQuestionResponseModal, LessonModal, CourseExamTimeoutModal, FeedbackModal, LessonSlideshowModal, LoadingSpinner, StartCourseForFreeModal, SpeakToExpertModal, BrowserExtensionModal, GetStartedOnboardingModal, AssignCourseGroupModal, SkgQuizModal, FollowupModal, CreateCourseModal, CreateOrUpdateCourseGroupModal, EditOrDeleteCourseGroupModal, MoveCourseToSectionModal, ReferralCodeModal, ExamAppointmentModal, CourseChangesModal, IDDCoursesCheckoutModal, GuidedQuizRegisterModal, LearningProgressPolicyModal, CourseDetailsModal, CompanyOnboardingModal, WebinarModal, useCommonMixin, useI18n, useAuthentication, useBreakpoint, useEditor, useCourse, useGamification, useToursAndHints, useSegment, useEditorModeManager, ThemeApi, UsersApi, UserModule, BookmarksModule, CourseModule, EditorModule, eventBus, CookieHelper, auth_config, TOURS_AND_HINTS }
}

})